/**
 * [T270-PY] DayOfWeekRedesignSkin — composes 4 KPI hero cards + a CSS-Grid
 * heatmap for the redesigned Day-of-Week tab on `/transactions`.
 *
 * Contract authority: `docs/superpowers/specs/2026-08-03-day-of-week-insights-design.md`
 *
 * Layout:
 *   - 4 KPI cards in `grid-cols-2 md:grid-cols-4 gap-2`
 *   - Single heatmap renders below and above 768px. The previous mobile
 *     7-row list branch was removed in T270-PY so the same surface, keyboard
 *     model, test IDs, KPI cards, loading state, error state, empty state, and
 *     drill-through contracts are used at every viewport.
 *
 * @module transactions/DayOfWeekRedesignSkin
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { BarChart2, TrendingDown, TrendingUp } from 'lucide-react';
import { useCurrency } from '@/hooks/useCurrency';
import {
  SkinEmptyState,
  SkinErrorState,
  SkinLoadingBars,
} from '@/components/dashboard/widgets/skins/insights/_shared';

import type {
  DayOfWeekBucket,
  DayOfWeekPayload,
} from '@/lib/insightsContracts';
import { DayOfWeekKpiCard } from './DayOfWeekKpiCard';
import { DayOfWeekCategoryStackChart } from './DayOfWeekCategoryStackChart';
import {
  DayOfWeekHeatmap,
  buildCategoryRows,
  computeMaxCellTotal,
  type DayOfWeekCellDrillInput,
  type DayOfWeekHeatmapCategoryRow,
  type DayOfWeekHeatmapDay,
} from './DayOfWeekHeatmap';

/**
 * Drill-through callback contract — mirrors design spec §4.
 * The skin MUST NOT inline URL serialization; the lead's `drillthrough-filters`
 * teammate owns the URL shape.
 */
export type DayOfWeekDrillInput =
  | {
      kind: 'topDay';
      dayOfWeek: number;
      windowStart: string;
      windowEnd: string;
    }
  | {
      kind: 'topCategory';
      categoryId: string;
      categoryName: string;
      windowStart: string;
      windowEnd: string;
    }
  | { kind: 'weekdayAvg'; windowStart: string; windowEnd: string }
  | { kind: 'weekendAvg'; windowStart: string; windowEnd: string }
  | {
      kind: 'cell';
      dayOfWeek: number;
      categoryId: string | '__other__';
      windowStart: string;
      windowEnd: string;
    };

/** Public props for the redesign skin. */
export interface DayOfWeekRedesignSkinProps {
  /** `null` while data is loading or in error state. */
  data: DayOfWeekPayload | null;
  loading: boolean;
  /** Optional error envelope (matches `SkinError` from insights skins). */
  error?: { message: string } | string | null;
  /** Optional retry callback forwarded to `SkinErrorState`. */
  onRetry?: () => void;
  /** ISO date — start of the day-of-week window. */
  windowStart?: string;
  /** ISO date — end of the day-of-week window. */
  windowEnd?: string;
  /**
   * Injected "now" so SSR / tests can pin `today.getDay()`.
   * Defaults to `new Date()` per the contract's `today` injection rule.
   */
  today?: Date;
  /**
   * When omitted, every KPI card AND every heatmap cell renders `disabled=true`.
   * Activation is wrapped in `try/catch` and guarded against double-fire so the
   * QA single-fire probe (`window.__dowDrillCalls`) increments exactly once per
   * user action sequence.
   */
  buildDrillHref?: (input: DayOfWeekDrillInput) => string;
}

/** Testid prefix for every QA selector in this skin. */
const TESTID_PREFIX = 'day-of-week-redesign';

/** Testid prefix for the nested heatmap grid; matches `DayOfWeekHeatmap`'s contract default
 *  so cells render as `day-of-week-redesign-heatmap-cell-{categoryId}-{dow}`. */
const HEATMAP_TESTID_PREFIX = 'day-of-week-redesign-heatmap';

/** Default window dates when the host adapter omits them. */
const DEFAULT_WINDOW = { start: '1970-01-01', end: '1970-01-01' };

/**
 * Resolve the canonical Mon..Sun day order from a `DayOfWeekPayload`.
 * `DayOfWeekBucket.dayOfWeek` is 1 (Sun) … 7 (Sat) per Mongo's `$dayOfWeek`.
 * We sort by day index so the heatmap always renders Mon..Sun (a finance-app
 * convention that groups weekend visually).
 */
function resolveDays(payload: DayOfWeekPayload): DayOfWeekHeatmapDay[] {
  const byDow = new Map<number, DayOfWeekBucket>();
  for (const d of payload.days) {
    byDow.set(d.dayOfWeek, d);
  }
  const order: number[] = [2, 3, 4, 5, 6, 7, 1];
  return order.map((dow) => {
    const bucket = byDow.get(dow);
    return {
      dayOfWeek: dow,
      dayName: bucket?.dayName ?? defaultDayName(dow),
    };
  });
}

function defaultDayName(dow: number): string {
  return ['', 'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][dow] ?? '';
}

/**
 * Convert `Date.getDay()` (0=Sun..6=Sat) → Mongo `$dayOfWeek` (1=Sun..7=Sat).
 * Pure helper — isolated so tests can pin it.
 */
function currentDowFromDate(d: Date): number {
  return d.getDay() + 1;
}

/** Global probe key used by the AC-I9 single-fire contract. */
const DRILL_PROBE_KEY = '__dowDrillCalls';

interface DrillProbeWindow {
  [key: string]: number | undefined;
}

function readProbe(): number {
  if (typeof window === 'undefined') return 0;
  const w = window as unknown as DrillProbeWindow;
  return typeof w[DRILL_PROBE_KEY] === 'number' ? (w[DRILL_PROBE_KEY] as number) : 0;
}

function writeProbe(value: number): void {
  if (typeof window === 'undefined') return;
  const w = window as unknown as DrillProbeWindow;
  w[DRILL_PROBE_KEY] = value;
}

export function DayOfWeekRedesignSkin({
  data,
  loading,
  error,
  onRetry,
  windowStart,
  windowEnd,
  today,
  buildDrillHref,
}: DayOfWeekRedesignSkinProps) {
  const { fmt, currency } = useCurrency();
  const currentDow = currentDowFromDate(today ?? new Date());

  // Stable reference for the heatmap.
  const days = useMemo<DayOfWeekHeatmapDay[]>(
    () => (data ? resolveDays(data) : []),
    [data],
  );
  const rows = useMemo<DayOfWeekHeatmapCategoryRow[]>(
    () => (data ? buildCategoryRows(data.days, 5) : []),
    [data],
  );
  const maxCellTotal = useMemo(() => computeMaxCellTotal(rows), [rows]);


  const dateWindow = {
    start: windowStart ?? DEFAULT_WINDOW.start,
    end: windowEnd ?? DEFAULT_WINDOW.end,
  };

  const isEmpty =
    !loading &&
    !error &&
    (!data ||
      data.days.length === 0 ||
      data.days.every((d: DayOfWeekBucket) => d.totalSpent <= 0));

  // Prime the AC-I9 single-fire probe on first mount so QA can verify that
  // the activation handler is invoked exactly once per user action sequence.
  useEffect(() => {
    writeProbe(0);
  }, []);

  // Per-input activation guard — prevents Enter + click from incrementing
  // the probe twice for the same drill-through target.
  const activatedRef = useRef<Set<string>>(new Set());

  const fireActivation = useCallback(
    (input: DayOfWeekDrillInput) => {
      if (!buildDrillHref) return;
      const key = JSON.stringify(input);
      if (activatedRef.current.has(key)) return;
      activatedRef.current.add(key);
      writeProbe(readProbe() + 1);
      try {
        const href = buildDrillHref(input);
        if (href) {
          window.location.assign(href);
        }
      } catch {
        window.location.assign('/transactions');
      }
    },
    [buildDrillHref],
  );

  /** Returns a click handler for the given drill input. */
  const activate = useCallback(
    (input: DayOfWeekDrillInput) => () => {
      fireActivation(input);
    },
    [fireActivation],
  );

  /**
   * Wrap the host-provided buildDrillHref so a cell click also increments
   * the QA single-fire probe and re-uses the same guarded activation path.
   * The wrapper returns the resolved URL so the heatmap can perform the
   * navigation itself; the probe is updated first so the contract test
   * observes the increment regardless of navigation.
   */
  const cellBuildDrillHref = useMemo(() => {
    if (!buildDrillHref) return undefined;
    return (input: DayOfWeekCellDrillInput): string => {
      const key = JSON.stringify({ ...input, kind: 'cell' });
      if (!activatedRef.current.has(key)) {
        activatedRef.current.add(key);
        writeProbe(readProbe() + 1);
      }
      return buildDrillHref({ ...input, kind: 'cell' } as DayOfWeekDrillInput);
    };
  }, [buildDrillHref]);

  // ─── Render ────────────────────────────────────────────────────────────
  // Loading / error / empty states — mirrors `DayOfWeekBarSkin` shape so
  // both surfaces render predictably. Testids are unique to the redesign so
  // QA can target them without colliding with the parity surface.
  if (loading) {
    return (
      <div
        role="status"
        aria-live="polite"
        data-testid={`${TESTID_PREFIX}-skin-loading`}
      >
        <span className="sr-only">Loading spending by day of week</span>
        <SkinLoadingBars bars={7} />
      </div>
    );
  }
  if (error) {
    const message = typeof error === 'string' ? error : error.message;
    return (
      <div
        role="status"
        aria-live="polite"
        data-testid={`${TESTID_PREFIX}-skin-error`}
      >
        <span className="sr-only">Error loading spending by day of week</span>
        <SkinErrorState message={message} onRetry={onRetry} />
      </div>
    );
  }
  if (isEmpty) {
    return (
      <div
        role="status"
        aria-live="polite"
        data-testid={`${TESTID_PREFIX}-skin-empty`}
      >
        <span className="sr-only">
          No spending data available for this period
        </span>
        <SkinEmptyState
          title="No spending data for this period"
          description="Add expense transactions to see your day-of-week pattern."
          cta={{ label: 'Add expense', to: '/transactions' }}
        />
      </div>
    );
  }

  if (!data) {
    // Defensive — unreachable because `isEmpty` covers this, but keeps TS happy.
    return null;
  }

  // ─── KPI labels & values ──────────────────────────────────────────────
  const topDay = data.highestDay;
  const topCategory = data.highestCategory;
  const weekdayKpi = (
    <DayOfWeekKpiCard
      testId={`${TESTID_PREFIX}-kpi-weekday-avg`}
      label="Weekday avg"
      value={fmt(data.weekdayAverage)}
      subValue="Mon–Fri mean"
      icon={<TrendingUp className="h-3 w-3" aria-hidden="true" />}
      tone="positive"
      disabled={loading || !buildDrillHref}
      onActivate={
        buildDrillHref
          ? activate({
              kind: 'weekdayAvg',
              windowStart: dateWindow.start,
              windowEnd: dateWindow.end,
            })
          : undefined
      }
    />
  );
  const weekendKpi = (
    <DayOfWeekKpiCard
      testId={`${TESTID_PREFIX}-kpi-weekend-avg`}
      label="Weekend avg"
      value={fmt(data.weekendAverage)}
      subValue="Sat+Sun mean"
      icon={<TrendingDown className="h-3 w-3" aria-hidden="true" />}
      tone="warning"
      disabled={loading || !buildDrillHref}
      onActivate={
        buildDrillHref
          ? activate({
              kind: 'weekendAvg',
              windowStart: dateWindow.start,
              windowEnd: dateWindow.end,
            })
          : undefined
      }
    />
  );
  const topDayKpi = (
    <DayOfWeekKpiCard
      testId={`${TESTID_PREFIX}-kpi-top-day`}
      label="Top day"
      value={topDay ? topDay.dayName.slice(0, 3) : '—'}
      subValue={topDay ? fmt(topDay.totalSpent) : undefined}
      icon={<BarChart2 className="h-3 w-3" aria-hidden="true" />}
      tone="neutral"
      disabled={loading || !buildDrillHref || !topDay}
      onActivate={
        buildDrillHref && topDay
          ? activate({
              kind: 'topDay',
              dayOfWeek: topDay.dayOfWeek,
              windowStart: dateWindow.start,
              windowEnd: dateWindow.end,
            })
          : undefined
      }
    />
  );
  const topCategoryKpi = (
    <DayOfWeekKpiCard
      testId={`${TESTID_PREFIX}-kpi-top-category`}
      label="Top category"
      value={
        topCategory
          ? `${topCategory.emoji ? `${topCategory.emoji} ` : ''}${topCategory.name}`
          : '—'
      }
      subValue={topCategory ? fmt(topCategory.total) : undefined}
      icon={<TrendingUp className="h-3 w-3" aria-hidden="true" />}
      tone="neutral"
      disabled={loading || !buildDrillHref || !topCategory}
      onActivate={
        buildDrillHref && topCategory
          ? activate({
              kind: 'topCategory',
              categoryId: topCategory.categoryId,
              categoryName: topCategory.name,
              windowStart: dateWindow.start,
              windowEnd: dateWindow.end,
            })
          : undefined
      }
    />
  );

  return (
    <div className="flex w-full min-w-0 flex-col gap-3">
      {/* KPI hero row — 2×2 mobile, 1×4 md+ */}
      <div
        className="grid grid-cols-2 gap-2 md:grid-cols-4"
        data-testid={`${TESTID_PREFIX}-kpis`}
        aria-label="Day of week summary"
      >
        {weekdayKpi}
        {weekendKpi}
        {topDayKpi}
        {topCategoryKpi}
      </div>
      {/* Phase C stacked-bar chart — informational, sits between the KPI hero row and the heatmap. */}
      <DayOfWeekCategoryStackChart days={days} rows={rows} fmt={fmt} />

      {/* Primary visual: the same heatmap renders at every viewport. */}
      <DayOfWeekHeatmap
        days={days}
        rows={rows}
        maxCellTotal={maxCellTotal}
        currentDow={currentDow}
        fmt={fmt}
        currency={currency}
        buildDrillHref={cellBuildDrillHref}
        windowStart={dateWindow.start}
        windowEnd={dateWindow.end}
        testIdPrefix={HEATMAP_TESTID_PREFIX}
      />
    </div>
  );
}

export default DayOfWeekRedesignSkin;
